import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	Dialog,
	Form,
	FormItem,
	TextArea,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	doesNotExceedLength,
	inline,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { localize } from "client_legacy/ndaw/culture";
import {
	createSharedSetting,
	IntegrationSharedSettingOptionModel,
	IntegrationSharedSettingSaveModel,
	updateSharedSetting
} from "client/Api/IntegrationBuilder/integrationBuilderApps";
import { useInfoPlaceHoldersDialog } from "./InfoPlaceHoldersDialog";
import { isNil, isNotNil } from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";

export type SharedSettingDialogProps = {
	appId: number;
	initialData?: IntegrationSharedSettingOptionModel;
	existingNames: readonly string[];
	onClose: () => void;
	onSave: (data: IntegrationSharedSettingOptionModel) => void;
};

export const SharedSettingDialog = (props: SharedSettingDialogProps): ReactElement => {
	const {
		appId,
		initialData,
		existingNames,
		onClose,
		onSave
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [model, setModel] = useState<IntegrationSharedSettingSaveModel>(
		() => isNotNil(initialData)
			? convertToSaveModel(initialData)
			: EMPTY_FORM_VALUE
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [infoDialog, openInfoDialog] = useInfoPlaceHoldersDialog();

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<IntegrationSharedSettingSaveModel> => {
			const result: IValidationConfiguration<IntegrationSharedSettingSaveModel> = staticValidationConfiguration.clone();

			result.forScalar("displayName")
				.add(
					isNotEmpty(),
					isNotNullNorUndefined(),
					doesNotExceedLength(255),
					inline(displayNameUniquenessValidation(existingNames))
				);

			return result;
		},
		[existingNames]
	);

	const formHandler: IFormHandler<IntegrationSharedSettingSaveModel> = useFormHandler<IntegrationSharedSettingSaveModel>(
		model,
		setModel,
		validationConfiguration
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			const validationResult = await formHandler.validation
				.validate(globalCancellation);

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}

			setIsBusy(true);

			try {
				if (isNil(initialData)) {
					const id: number = await createSharedSetting(
						appId,
						model,
						globalCancellation
					);

					onSave({
						...model,
						id
					});
				} else {
					await updateSharedSetting(
						initialData.id,
						model,
						globalCancellation
					);

					onSave({
						...model,
						id: initialData.id
					});
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!formHandler.validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			appId,
			formHandler,
			globalCancellation,
			initialData,
			isBusy,
			model,
			onSave
		]
	);

	return (
		<>
			<Dialog
				trackingId="integration-shared-setting-dialog"
			>
				<Dialog.Header
					title={localize(
						isNil(initialData)
							? "_NewSharedSetting"
							: "_EditSharedSetting"
					)}
					onShowInfo={() => openInfoDialog({})}
					onClose={onClose}
				/>
				<Dialog.Body>
					<Form onSubmit={saveHandler}>
						<FormItem label={localize("_DisplayName")} isRequired>
							<TextInput
								{...formHandler.for("displayName")
									.attachToTextInput()
								}
								placeholder={localize("_EnterDisplayName")}
							/>
							<ValidationError
								{...formHandler.for("displayName").attachToValidationError()}
							/>
						</FormItem>
						<FormItem label={localize("_Value")} isRequired>
							<TextInput
								{...formHandler.for("value")
									.attachToTextInput()
								}
								placeholder={localize("_EnterValue")}
							/>
							<ValidationError
								{...formHandler.for("value").attachToValidationError()}
							/>
						</FormItem>
						<FormItem label={localize("_Description")}>
							<TextArea
								{...formHandler.for("description")
									.attachToTextArea()
								}
								placeholder={localize("_EnterDescription")}
							/>
							<ValidationError
								{...formHandler.for("description").attachToValidationError()}
							/>
						</FormItem>
					</Form>
				</Dialog.Body>
				<Dialog.Footer>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={localize("_Save")}
							isDisabled={isBusy}
							onClick={saveHandler}
							trackingId="save"
						/>
						<Toolbar.Button
							kind="secondary"
							text={localize("_Cancel")}
							onClick={onClose}
							trackingId="cancel"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
			{infoDialog}
		</>
	);
};
SharedSettingDialog.displayName = "SharedSettingDialog";

export type SharedSettingDialogInitialData = Omit<SharedSettingDialogProps, "onClose" | "onSave">;

export function useSharedSettingDialog(): [
	element: ReactElement | null,
	open: (props: SharedSettingDialogInitialData) => Promise<TryResult<IntegrationSharedSettingOptionModel>>
] {
	return useDataInputDialog<SharedSettingDialogInitialData, IntegrationSharedSettingOptionModel>(
		(props, onClose, onSave) => (
			<SharedSettingDialog
				appId={props.appId}
				initialData={props.initialData}
				existingNames={props.existingNames}
				onClose={onClose}
				onSave={onSave}
			/>
		)
	);
}

function convertToSaveModel(
	sharedSetting: IntegrationSharedSettingOptionModel
): IntegrationSharedSettingSaveModel {
	return {
		value: sharedSetting.value,
		displayName: sharedSetting.displayName,
		description: sharedSetting.description ?? ""
	};
}

function displayNameUniquenessValidation(
	existingNames: readonly string[]
): (value: string) => InputValidationResult {
	return (value) => {
		const isDuplicate: boolean = existingNames.some(
			x => x.toLocaleLowerCase() === value.toLocaleLowerCase()
		);

		if (isDuplicate) {
			return {
				kind: InputValidationResultKind.Invalid,
				errorMessage: localize("_IsAlreadyBeingUsedAndMustBeUnique")
			};
		}

		return { kind: InputValidationResultKind.Valid };
	};
}

const EMPTY_FORM_VALUE: IntegrationSharedSettingSaveModel = {
	value: "",
	displayName: "",
	description: ""
};

const staticValidationConfiguration = createValidationConfiguration<IntegrationSharedSettingSaveModel>();

staticValidationConfiguration.forScalar("value")
	.add(
		isNotEmpty(),
		isNotNullNorUndefined(),
		doesNotExceedLength(2_000)
	);

staticValidationConfiguration.forScalar("description")
	.add(
		doesNotExceedLength(255)
	);
